import { useEffect } from 'react';

/**
 * React Hook for Python Terminal Theme
 * Automatically applies terminal styling to all Python code blocks
 */

export function usePythonGreenTheme() {
  useEffect(() => {
    const forcePythonTerminalTheme = () => {
      console.log('🎨 Applying Python Green Terminal Theme...');
      
      // Define the green terminal styles
      const terminalStyle = {
        backgroundColor: '#000000',
        color: '#00ff00',
        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
        border: '2px solid #00ff00',
        borderRadius: '8px',
        padding: '16px',
        fontSize: '14px',
        lineHeight: '1.5'
      };

      // Selectors for all possible Python code elements
      const selectors = [
        'pre',
        'code',
        '.python-code-editor',
        '.python-output', 
        '.python-force-white',
        '.interactive-python-block',
        '.python-block',
        '.code-block',
        '.hljs',
        '.highlight',
        'textarea[class*="python"]',
        '[class*="python-terminal"]'
      ];

      // Apply styles to all matching elements
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const htmlElement = element as HTMLElement;
          
          // Apply all styles with !important
          htmlElement.style.setProperty('background-color', terminalStyle.backgroundColor, 'important');
          htmlElement.style.setProperty('color', terminalStyle.color, 'important');
          htmlElement.style.setProperty('font-family', terminalStyle.fontFamily, 'important');
          htmlElement.style.setProperty('border', terminalStyle.border, 'important');
          htmlElement.style.setProperty('border-radius', terminalStyle.borderRadius, 'important');
          htmlElement.style.setProperty('padding', terminalStyle.padding, 'important');
          htmlElement.style.setProperty('font-size', terminalStyle.fontSize, 'important');
          htmlElement.style.setProperty('line-height', terminalStyle.lineHeight, 'important');
        });
        
        if (elements.length > 0) {
          console.log(`✅ Applied green terminal theme to ${elements.length} elements: ${selector}`);
        }
      });

      // Special handling for InteractivePythonBlock components
      const pythonBlocks = document.querySelectorAll('.interactive-python-block, [class*="InteractivePython"]');
      pythonBlocks.forEach(block => {
        const htmlBlock = block as HTMLElement;
        
        // Style the main container
        htmlBlock.style.setProperty('background-color', '#000000', 'important');
        htmlBlock.style.setProperty('border', '2px solid #00ff00', 'important');
        htmlBlock.style.setProperty('border-radius', '8px', 'important');
        
        // Style all child elements
        const children = block.querySelectorAll('*');
        children.forEach(child => {
          const htmlChild = child as HTMLElement;
          if (child.tagName === 'TEXTAREA' || child.tagName === 'PRE' || child.tagName === 'CODE') {
            htmlChild.style.setProperty('background-color', '#000000', 'important');
            htmlChild.style.setProperty('color', '#00ff00', 'important');
            htmlChild.style.setProperty('font-family', terminalStyle.fontFamily, 'important');
          }
        });
      });

      console.log('🎨 Green terminal theme applied successfully!');
    };

    // Apply immediately
    forcePythonTerminalTheme();
    
    // Apply again after short delays (for dynamic content)
    setTimeout(forcePythonTerminalTheme, 100);
    setTimeout(forcePythonTerminalTheme, 500);
    setTimeout(forcePythonTerminalTheme, 1000);
    
    // Set up mutation observer for dynamically added content
    const observer = new MutationObserver((mutations) => {
      let hasNewCodeBlocks = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const element = node as Element;
            const hasCode = element.matches && (
              element.matches('pre, code, .interactive-python-block, textarea') ||
              element.querySelector('pre, code, .interactive-python-block, textarea')
            );
            if (hasCode) {
              hasNewCodeBlocks = true;
            }
          }
        });
      });
      
      if (hasNewCodeBlocks) {
        console.log('🔄 New code blocks detected, reapplying green theme...');
        setTimeout(forcePythonTerminalTheme, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Cleanup function
    return () => {
      observer.disconnect();
      console.log('🧹 Python green theme cleanup');
    };
  }, []);
}

export function usePythonWhiteTheme() {
  useEffect(() => {
    const forcePythonWhiteTheme = () => {
      console.log('🎨 Applying Python White Terminal Theme...');
      
      const selectors = [
        'pre', 'code', '.python-code-editor', '.python-output',
        '.python-force-white', '.interactive-python-block', '.python-block', 
        '.code-block', 'textarea'
      ];

      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const htmlElement = element as HTMLElement;
          htmlElement.style.setProperty('background-color', '#000000', 'important');
          htmlElement.style.setProperty('color', '#ffffff', 'important');
          htmlElement.style.setProperty('border', '2px solid #ffffff', 'important');
          htmlElement.style.setProperty('font-family', 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace', 'important');
        });
        
        if (elements.length > 0) {
          console.log(`✅ Applied white terminal theme to ${elements.length} elements: ${selector}`);
        }
      });
    };

    // Apply immediately and repeatedly
    forcePythonWhiteTheme();
    setTimeout(forcePythonWhiteTheme, 100);
    setTimeout(forcePythonWhiteTheme, 500);
    setTimeout(forcePythonWhiteTheme, 1000);

    // Set up mutation observer
    const observer = new MutationObserver(() => {
      setTimeout(forcePythonWhiteTheme, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return () => {
      observer.disconnect();
      console.log('🧹 Python white theme cleanup');
    };
  }, []);
}

// Default export
export default usePythonGreenTheme;